import { default as React, useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import StringField from '../../../core/components/Fields/StringField.tsx';
import FormField from '../../../core/components/FormField.tsx';
import SaveStatus from '../../../core/components/SaveStatus.tsx';
import SettingsFormCard from '../../../core/components/SettingsFormCard.tsx';
import Switch from '../../../core/components/Switch.tsx';
import { useToaster } from '../../../core/components/Toast.tsx';
import ToggleList from '../../../core/components/ToggleList.tsx';
import useSectionSaveStatus from '../../../core/utils/hooks/useSectionSaveStatus.ts';
import UploadConfigFormFields from '../../../document/components/UploadConfigFormFields.tsx';
import { clearInapplicableUploadFields, useUploadConfigFieldState } from '../../../document/uploadConfigFields.ts';

const ADDITIONAL_FILE_TYPES = [
    'DELIVERY_NOTE',
    'INVOICE',
    'OFFER',
    'ORDER_CONFIRMATION',
    'REQUEST_FOR_QUOTATION',
    'OTHER',
];

const ADDITIONAL_FILES_FIELDS = ['sendAdditionalFiles', 'additionalFileTypes', 'targetPathAdditionalFiles'];

const FORM_DEFAULT_VALUES = {
    enabled: true,
    targetPathFileExport: '',
    targetPathOriginalDocument: '',
    sendOriginalDocument: true,
    sendOriginalEmail: false,
    deliveryMethod: 'SFTP',
    sendAdditionalFiles: false,
    additionalFileTypes: [],
    targetPathAdditionalFiles: '',
};

const OrderUploadConfig = ({ config, emailConfig, onActivate, onSubmit }) => {
    const { t } = useTranslation('config');
    const { publishToast } = useToaster();

    const form = useForm({ defaultValues: FORM_DEFAULT_VALUES, mode: 'onTouched' });
    const formErrors = form.formState.errors;
    const sendAdditionalFiles = form.watch('sendAdditionalFiles');
    const { isApplicable } = useUploadConfigFieldState(form);

    const showAdditionalFilesSection =
        (emailConfig?.createEmailParts || emailConfig?.upfrontClassification) && isApplicable('sendAdditionalFiles');

    useEffect(() => {
        if (!config) return;

        form.reset({
            enabled: config?.enabled,
            targetPathFileExport: config?.targetPathFileExport || '',
            targetPathOriginalDocument: config?.targetPathOriginalDocument || '',
            sendOriginalDocument: config?.sendOriginalDocument,
            sendOriginalEmail: config?.sendOriginalEmail,
            deliveryMethod: config?.deliveryMethod || '',
            sendAdditionalFiles: config?.sendAdditionalFiles || false,
            additionalFileTypes: config?.additionalFileTypes || [],
            targetPathAdditionalFiles: config?.targetPathAdditionalFiles || '',
        });
    }, [config]);

    const handleSubmit = async (formData) => {
        try {
            if (!config) await onActivate();
            await onSubmit(clearInapplicableUploadFields(formData));
            publishToast({ description: t('configPage.upload.updateSuccess'), status: 'success' });
        } catch (e) {
            console.error(e);
            publishToast({ description: t('configPage.upload.updateError'), status: 'error' });
        }
    };

    const { submitSection, getSaveStatusProps } = useSectionSaveStatus(form, handleSubmit);
    const uploadFields = Object.keys(FORM_DEFAULT_VALUES).filter((name) => !ADDITIONAL_FILES_FIELDS.includes(name));

    return (
        <div className="flex flex-col gap-6">
            <SettingsFormCard onSubmit={submitSection('upload')} title={t('configPage.upload.title')}>
                <UploadConfigFormFields form={form} />

                <SaveStatus {...getSaveStatusProps('upload', uploadFields)} />
            </SettingsFormCard>

            {showAdditionalFilesSection && (
                <SettingsFormCard
                    onSubmit={submitSection('additionalFiles')}
                    title={t('configPage.upload.form.sendAdditionalFiles')}
                    description={t('configPage.upload.form.sendAdditionalFilesDescription')}
                    controls={
                        <Switch
                            value={sendAdditionalFiles}
                            onValueChange={(value) => {
                                form.setValue('sendAdditionalFiles', value, { shouldDirty: true });
                                if (!value) {
                                    form.setValue('additionalFileTypes', [], { shouldDirty: true });
                                    form.setValue('targetPathAdditionalFiles', '', { shouldDirty: true });
                                }
                            }}
                        />
                    }
                    className="gap-0"
                >
                    <div className="mt-6 flex flex-col gap-6">
                        {sendAdditionalFiles && (
                            <>
                                <Controller
                                    name="additionalFileTypes"
                                    control={form.control}
                                    render={({ field }) => (
                                        <FormField
                                            label={t('configPage.upload.form.additionalFileTypes')}
                                            error={formErrors.additionalFileTypes}
                                        >
                                            <ToggleList
                                                value={field.value}
                                                onValueChange={(value) => field.onChange({ target: { value } })}
                                                onBlur={field.onBlur}
                                                items={ADDITIONAL_FILE_TYPES}
                                                translationPrefix="configPage.upload.additionalFileTypeNames"
                                            />
                                        </FormField>
                                    )}
                                />

                                {isApplicable('targetPathAdditionalFiles') && (
                                    <Controller
                                        name="targetPathAdditionalFiles"
                                        control={form.control}
                                        render={({ field }) => (
                                            <FormField
                                                label={t('configPage.upload.form.targetPathAdditionalFiles')}
                                                error={formErrors.targetPathAdditionalFiles}
                                            >
                                                <StringField {...field} />
                                            </FormField>
                                        )}
                                    />
                                )}
                            </>
                        )}

                        <SaveStatus {...getSaveStatusProps('additionalFiles', ADDITIONAL_FILES_FIELDS)} />
                    </div>
                </SettingsFormCard>
            )}
        </div>
    );
};

export default OrderUploadConfig;
